import { Component } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { 
  ListHomeDetailsRequest, ListHomeDetailsResponse, ProductName, SituViewWebApiClient, StyleConfigTemplate, HomeDetail, ListTagsRequest, ListTagsResponse, Tag,
  UpdateHomeRequest,
  UpdateHomeResponse
} from 'situview-webapi-client';
import { AuthService, StateService, Utils } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';

@Component({
  selector: 'home-details-list',
  templateUrl: './home-details-list.component.html',
  styleUrls: ['./home-details-list.component.less']
})
export class HomeDetailsListComponent extends SituBaseComponent {
  loading: boolean = false;
  data = new MatTableDataSource<HomeDetail>();
  searchTerm = "";
  productNames = ProductName;
  productName: ProductName | undefined;
  //isHidden: boolean | undefined;
  taggedOnly: boolean = false;

  constructor(
    authService: AuthService,
    stateService: StateService,
    private router: Router,
    private snackBar: MatSnackBar,
    private situViewApiClient: SituViewWebApiClient
  ) { 
    super(authService, stateService);
  }

  protected onSelectedCompanyChange(): void {
    this.loadTags()
      .then(() => {
        this.loadData();
      });
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  downloadCSV() {
    if (!this.data.data) {
      return;
    }

    const csvData = this.convertToCSV(this.data.data);
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const timestamp = Utils.getCurrentTimestamp();
    const fileName = `home_list_${timestamp}.csv`;

    // For IE 11
    if ((navigator as any).msSaveBlob) {
      (navigator as any).msSaveBlob(blob, fileName);
    } else {
      // Modern browsers
      const link = document.createElement('a');
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  }  
  private convertToCSV(homeDetails: HomeDetail[]): string {
    let str = '';
    let row = '';

    const headers = ['Home', 'Enabled', 'RenderedThisMonth', 'RaytracePlusThisMonth'];
    row = headers.join(',') + '\r\n';
    str += row;

    // Initialize counters for totals
    let enabledCount = 0;
    let renderedThisMonthCount = 0;
    let raytracePlusThisMonthCount = 0;

    homeDetails.forEach((item: HomeDetail) => {
      let line = '';
      
      const isEnabled = !item.isHidden;
      const isRenderedThisMonth = this.isRenderedThisMonth(item.dateLastRendered);
      const isRaytracePlusThisMonth = this.isRenderedThisMonth(item.dateLastRaytracedPlusRendered);
      
      // Count true values
      if (isEnabled) enabledCount++;
      if (isRenderedThisMonth) renderedThisMonthCount++;
      if (isRaytracePlusThisMonth) raytracePlusThisMonthCount++;
      
      line += `${item.name},`;
      line += `${isEnabled},`;
      line += `${isRenderedThisMonth},`;
      line += `${isRaytracePlusThisMonth}`;
      str += line + '\r\n';
    });

    // Add totals row
    str += `TOTALS,${enabledCount},${renderedThisMonthCount},${raytracePlusThisMonthCount}\r\n`;

    return str;
  }

  getTagLabel(tagId: string): string {
    let tag = this.tags?.find(x => x.id === tagId);
    if (tag) {
      return `${tag.name}`;
    }
    return "";
  }
  
  getTagCategory(tagId: string): string {
    let tag = this.tags?.find(x => x.id === tagId);
    if (tag) {
      return `${tag?.category?.name}`;
    }
    return "";
  }

  onClickStyleTemplate(item: StyleConfigTemplate) {
    this.router.navigate(['admin/style-template', item.id]);
  }

  setProductName(productName: ProductName) {
    this.productName = productName;
    this.loadData();
  }
  
  // setIsHidden(isHidden: boolean) {
  //   this.isHidden = isHidden;
  //   this.loadData();
  // }
  
  setTaggedOnly() {
    this.taggedOnly = !this.taggedOnly;
    this.loadData();
  }

  showOnlyVisible: boolean | undefined = true;
  toggleVisibility() {
    if (this.showOnlyVisible === true) {
      this.showOnlyVisible = undefined;
    } else {
      this.showOnlyVisible = true;
    }
    this.loadData();
  }

  toggleHomeVisibility(home: HomeDetail) {
    if (!home) {
      this.showMessage("Home is required!");
      return;
    }
    this.loading = true;

    let request = new UpdateHomeRequest();
    request.companyId = this.selectedCompany?.id;
    request.homeId = home.id;
    request.isHidden = !home.isHidden;

    this.situViewApiClient.updateHome(request)
      .then((response: UpdateHomeResponse) => {
        if (response.success) {
          home.isHidden = !home.isHidden;
        } else {
          this.showMessage("There was an issue updating home details!");
        }
        this.loading = false;
      })
      .catch((err: any) => {
        this.loading = false;
        this.showMessage("Could not retrieve home details!");
      });
  }

  loadData() {
    this.loading = true;

    let request = new ListHomeDetailsRequest();
    request.companyId = this.selectedCompany?.id;
    if (this.taggedOnly) request.excludeHomesWithNoTags = true;
    if (this.searchTerm) request.searchTerm = this.searchTerm;
    if (this.showOnlyVisible !== undefined) request.isHidden = !this.showOnlyVisible;

    this.situViewApiClient.listHomeDetails(request)
      .then((response: ListHomeDetailsResponse) => {
        if (response.success) {
          this.data.data = response.items ?? [];
        } else {
          this.showMessage("There was an issue retrieving home details!");
        }
        this.loading = false;
      })
      .catch((err: any) => {
        this.loading = false;
        this.showMessage("Could not retrieve home details!");
      });
  }

  tags: Tag[] | undefined;
  private loadTags() {
    let request = new ListTagsRequest();
    request.companyId = this.selectedCompany?.id;

    return this.situViewApiClient.listTags(request)
      .then((response: ListTagsResponse) => {
        if (response.success) {
          this.tags = response.tags;
        }
      })
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }

  isRenderedThisMonth(date: Date | undefined): boolean {
    if (!date) return false;
    
    const renderDate = new Date(date);
    const now = new Date();
    
    return renderDate.getMonth() === now.getMonth() && 
           renderDate.getFullYear() === now.getFullYear();
  }

  formatDate(date: Date | undefined): string {
    if (!date) return '';
    
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  }
}